import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './pages/CreateCollectionPage.css';
import { useWallet } from '@solana/wallet-adapter-react';
import { useCreatorPublishing } from '../hooks/useCreatorPublishing';
import { assertPublishingNetwork, authenticatePublishingWallet, callDigitalAssets, executePublishingOperation, formatAtomic, pendingPublishingOperation } from '../utils/digitalAssetsClient';

// Umi / Core / Toolbox
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import {
    createGenericFile,
    sol,
    publicKey as umiPublicKey,
} from '@metaplex-foundation/umi';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
import { mplCore } from '@metaplex-foundation/mpl-core';
import { irysUploader } from '@metaplex-foundation/umi-uploader-irys';

const DEVNET_IRYS = 'https://devnet.irys.xyz';
const MAINNET_IRYS = 'https://uploader.irys.xyz';


const pctToBp = (pct) => {
    const n = Number(pct);
    const clamped = Number.isFinite(n) ? Math.max(0, Math.min(20, n)) : 0; // 0..20%
    return Math.round(clamped * 100);
};

/** Returns { basisPoints, creators } for a Royalties plugin.
 * ownerPk (string) is the connected wallet; always included.
 * otherCreators: [{address, percentage}] percentages must sum ≤100.
 * Percentages are integer shares out of 100 (Core requirement).
 */
const normalizeRoyalties = (ownerPk, royaltyPercent, otherCreators, toUmiPk) => {
    const basisPoints = pctToBp(royaltyPercent ?? 0);
    if (basisPoints <= 0) return { basisPoints: 0, creators: [] };

    const others = [];
    for (const c of otherCreators || []) {
        const pct = Number(c?.percentage ?? 0);
        const addr = String(c?.address || '').trim();
        if (!addr || !Number.isFinite(pct) || pct <= 0) continue;
        try {
            toUmiPk(addr); // validate parseable
            others.push({ address: addr, pct });
        } catch {
            // skip invalid
        }
    }

    let sumOthers = others.reduce((s, x) => s + x.pct, 0);
    if (sumOthers > 99) {
        others.forEach((o) => (o.pct = (o.pct / sumOthers) * 99)); // ensure owner gets ≥1
        sumOthers = 99;
    }
    let ownerPct = 100 - Math.round(sumOthers);
    if (ownerPct < 1) ownerPct = 1;

    const bucket = new Map();
    const addShare = (addr, pct) => {
        const k = toUmiPk(addr).toString();
        bucket.set(k, (bucket.get(k) ?? 0) + pct);
    };
    addShare(ownerPk, ownerPct);
    others.forEach((o) => addShare(o.address, o.pct));

    let creators = [...bucket.entries()].map(([address, pct]) => ({
        address: toUmiPk(address),
        percentage: Math.max(0, Math.round(pct)),
    }));

    // drift correction
    let drift = 100 - creators.reduce((s, c) => s + c.percentage, 0);
    if (drift !== 0 && creators.length) {
        creators[0].percentage = Math.max(0, creators[0].percentage + drift);
    }
    creators = creators.filter((c) => c.percentage > 0);

    if (creators.length === 0) {
        creators = [{ address: toUmiPk(ownerPk), percentage: 100 }];
    }
    return { basisPoints, creators };
};

export default function CreateCollectionForm() {
    const walletContext = useWallet();
    const { publicKey, wallet, connected } = walletContext;
    const spaceId = publicKey?.toBase58?.() || '';
    const { space, collections } = useCreatorPublishing(spaceId);
    const [configuration, setConfiguration] = useState(null);
    const [sourceImageUrl, setSourceImageUrl] = useState('');
    const [authorityWallet, setAuthorityWallet] = useState(spaceId);
    const [supportsCompressed, setSupportsCompressed] = useState(false);
    const defaultsWallet = useRef('');
    const edited = useRef({});
    const uploads = useRef({});
    useEffect(() => {
        callDigitalAssets('getConfiguration').then(setConfiguration).catch(error => setError(error.message));
    }, []);
    useEffect(() => {
        if (!space || !spaceId || defaultsWallet.current === spaceId) return;
        defaultsWallet.current = spaceId;
        if (!edited.current.name) setName(space.pageTitle || space.muultiName || '');
        if (!edited.current.description) setDescription(space.pageDescription || '');
        const image = space.profilePicUrl || (space.headerBackground?.type === 'image' ? space.headerBackground.imageUrl : '') || '';
        if (!edited.current.image) { setSourceImageUrl(image); setPreviewUrl(image); }
        if (!edited.current.authority) setAuthorityWallet(spaceId);
    }, [space, spaceId]);

    // network + irys
    const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT || 'https://api.devnet.solana.com';
    const isDevnet = configuration?.cluster === 'devnet';
    const irysAddress = isDevnet ? DEVNET_IRYS : MAINNET_IRYS;

    // form
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [attributes, setAttributes] = useState([]);

    // drag & drop + preview
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isDragActive, setIsDragActive] = useState(false);

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (f && f.type.startsWith('image/')) {
            edited.current.image = true;
            setFile(f);
            const url = URL.createObjectURL(f);
            setPreviewUrl(url);
        }
    };
    const onDrop = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        setIsDragActive(false);
        const f = evt.dataTransfer?.files?.[0];
        if (f && f.type.startsWith('image/')) {
            edited.current.image = true;
            setFile(f);
            const url = URL.createObjectURL(f);
            setPreviewUrl(url);
        }
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };
    useEffect(() => {
        return () => {
            if (previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // royalties
    const [royaltyPercent, setRoyaltyPercent] = useState(7); // 0..20 (%)
    const [otherCreators, setOtherCreators] = useState([]); // [{address, percentage}]
    const othersTotal = otherCreators.reduce((s, r) => s + (Number(r.percentage) || 0), 0);

    // UX state
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const fundingTxSig = error.match(/failed to post funding tx - ([1-9A-HJ-NP-Za-km-z]{87,88})/)?.[1];
    const [minting, setMinting] = useState(false);
    const [steps, setSteps] = useState({
        imageUploaded: false,
        metadataUploaded: false,
        serviceFeePaid: false,
        collectionCreated: false,
        royaltiesAttached: false,
    });
    const [txSig, setTxSig] = useState('');
    const [collectionPk, setCollectionPk] = useState('');
    const [imageUri, setImageUri] = useState('');
    const [metadataUri, setMetadataUri] = useState('');

    // umi
    const umi = useMemo(() => {
        const base = createUmi(rpcEndpoint).use(mplCore());
        // The installed Irys SDK requests skipPreflight=true for funding. Keep
        // simulation enabled so invalid payments fail before being posted to Irys.
        const sendTransaction = base.rpc.sendTransaction.bind(base.rpc);
        base.rpc.sendTransaction = (transaction, options = {}) => sendTransaction(transaction, {
            ...options, skipPreflight: false, preflightCommitment: 'confirmed', maxRetries: options.maxRetries ?? 5,
        });
        if (wallet?.adapter) base.use(walletAdapterIdentity(wallet.adapter));
        base.use(irysUploader({ address: irysAddress }));
        return base;
    }, [wallet?.adapter, rpcEndpoint, irysAddress]);

    // file -> GenericFile
    const toGenericFile = useCallback(async () => {
        if (!file && !sourceImageUrl) return null;
        const response = !file ? await fetch(sourceImageUrl, { mode: 'cors' }) : null;
        if (response && !response.ok) throw new Error('The Space image could not be loaded. Choose an image file.');
        const image = file || await response.blob();
        const buf = new Uint8Array(await image.arrayBuffer());
        const mime = image.type || 'image/png';
        return createGenericFile(buf, file?.name || 'image', {
            tags: [{ name: 'Content-Type', value: mime }],
        });
    }, [file, sourceImageUrl]);

    // airdrop (devnet)
    const [airdropping, setAirdropping] = useState(false);
    const handleAirdrop = async () => {
        if (!isDevnet || !umi?.identity?.publicKey) return;
        try {
            setAirdropping(true);
            setStatus('Airdropping 1 SOL to your devnet wallet…');
            await assertPublishingNetwork('devnet', rpcEndpoint);
            await umi.rpc.airdrop(umi.identity.publicKey, sol(1));
            setStatus('Airdrop complete.');
        } catch (e) {
            console.error(e);
            setError(e.message || 'Airdrop failed.');
        } finally {
            setAirdropping(false);
        }
    };

    const addCreator = () => setOtherCreators((p) => [...p, { address: '', percentage: 0 }]);
    const updateCreator = (i, field, value) =>
        setOtherCreators((p) => p.map((c, idx) => (idx === i ? { ...c, [field]: field === 'percentage' ? Number(value) : value } : c)));
    const removeCreator = (i) => setOtherCreators((p) => p.filter((_, idx) => idx !== i));

    const canCreate =
        !!name.trim() &&
        !!description.trim() &&
        !!(file || sourceImageUrl) &&
        Number.isFinite(Number(royaltyPercent)) &&
        royaltyPercent >= 0 &&
        royaltyPercent <= 20 &&
        attributes.every(attribute => attribute.name.trim() && attribute.name.length <= 128 && String(attribute.value).length <= 2048) &&
        othersTotal <= 100;

    const handleCreate = async () => {
        setError('');
        setStatus('');
        setTxSig('');
        setCollectionPk('');
        setImageUri('');
        setMetadataUri('');
        setSteps({ imageUploaded: false, metadataUploaded: false, serviceFeePaid: false, collectionCreated: false, royaltiesAttached: false });

        if (!connected || !publicKey || !wallet?.adapter) {
            setError('Connect a wallet first.');
            return;
        }
        if (!pendingPublishingOperation(spaceId, 'collection') && (!name.trim() || !description.trim() || (!file && !sourceImageUrl))) {
            setError('Please fill in name, description, and upload an image.');
            return;
        }
        if (othersTotal > 100) {
            setError('Other creators percentages must sum to ≤ 100.');
            return;
        }

        try {
            setMinting(true);
            await authenticatePublishingWallet(walletContext);
            const pending = pendingPublishingOperation(spaceId, 'collection');
            if (pending) {
                let prepared = await callDigitalAssets('confirmOperation', { requestId: pending }, walletContext);
                if (['EXPIRED', 'FAILED'].includes(prepared.status)) prepared = await callDigitalAssets('retryCollection', { requestId: pending }, walletContext);
                setImageUri(prepared.image || '');
                setMetadataUri(prepared.metadataUri || '');
                setSteps(previous => ({ ...previous, imageUploaded: !!prepared.image, metadataUploaded: !!prepared.metadataUri }));
                const result = await executePublishingOperation(walletContext, prepared, 'collection', setStatus);
                setSteps(previous => ({ ...previous, serviceFeePaid: !!configuration?.fees.collectionCreationFeeEnabled && result.status === 'CONFIRMED',
                    collectionCreated: result.status === 'CONFIRMED', royaltiesAttached: result.status === 'CONFIRMED' && pctToBp(royaltyPercent) > 0 }));
                setTxSig(result.signature);
                setCollectionPk(result.assetAddress);
                setStatus(result.status === 'CONFIRMED' ? 'Collection saved and available as a Collection ID access option.' : result.error || 'Transaction pending. Retry to check confirmation.');
                return;
            }
            if (!configuration?.mintAuthorityWallet) throw new Error('Creator publishing is not configured yet.');
            await assertPublishingNetwork(configuration.cluster, rpcEndpoint);

            // 1) Upload image (Irys)
            setStatus('Preparing and uploading image…');
            if (uploads.current.file !== file || uploads.current.source !== sourceImageUrl) uploads.current = { file, source: sourceImageUrl };
            const saved = uploads.current;
            let imgUri = saved.imageUri;
            if (!imgUri) {
                const generic = await toGenericFile();
                if (!generic) throw new Error('No image file.');
                [imgUri] = await umi.uploader.upload([generic]);
                if (!imgUri) throw new Error('Image upload failed.');
                saved.imageUri = imgUri;
                saved.contentType = generic.tags?.find(t => t.name === 'Content-Type')?.value || 'image/png';
            }
            setImageUri(imgUri);
            setSteps((s) => ({ ...s, imageUploaded: true }));

            // 2) Upload metadata (Irys)
            const contentType = saved.contentType;
            const metadata = {
                name: name.trim(),
                description: description.trim(),
                image: imgUri,
                attributes: attributes.map(attribute => ({ trait_type: attribute.name.trim(), value: attribute.value })),
                properties: { files: [{ uri: imgUri, type: contentType }], category: 'image' },
            };
            setStatus('Uploading metadata…');
            const metadataKey = JSON.stringify(metadata);
            const mdUri = saved.metadataKey === metadataKey ? saved.metadataUri : await umi.uploader.uploadJson(metadata);
            if (!mdUri) throw new Error('Metadata upload failed.');
            saved.metadataKey = metadataKey;
            saved.metadataUri = mdUri;
            setMetadataUri(mdUri);
            setSteps((s) => ({ ...s, metadataUploaded: true }));

            // Keep Core creation and fees in one creator-signed transaction.
            const normalized = normalizeRoyalties(spaceId, royaltyPercent, otherCreators, umiPublicKey);
            const prepared = await callDigitalAssets('prepareCollection', {
                name: name.trim(), description: description.trim(), image: imgUri, metadataUri: mdUri,
                attributes: metadata.attributes,
                authorityWallet: authorityWallet.trim() || spaceId, supportsCompressed,
                creatorConfiguration: { basisPoints: pctToBp(royaltyPercent), creators: normalized.creators.length ? normalized.creators.map(creator => ({ address: String(creator.address), percentage: creator.percentage })) : [{ address: spaceId, percentage: 100 }] },
            }, walletContext);
            const result = await executePublishingOperation(walletContext, prepared, 'collection', setStatus);
            setTxSig(result.signature);
            setCollectionPk(result.assetAddress);
            setSteps(previous => ({ ...previous, serviceFeePaid: configuration.fees.collectionCreationFeeEnabled && result.status === 'CONFIRMED',
                collectionCreated: result.status === 'CONFIRMED', royaltiesAttached: pctToBp(royaltyPercent) > 0 }));
            setStatus(result.status === 'CONFIRMED' ? 'Collection saved and available as a Collection ID access option.' : result.error || 'Transaction pending. Retry to check confirmation.');
        } catch (e) {
            console.error(e);
            setStatus('');
            setError(e.message || 'Create collection failed.');
        } finally {
            setMinting(false);
        }
    };

    return (
        <div className="muulti-container collection-create-page">
            <h1>{collections.length ? 'Create another Collection' : space ? 'Create a collection for this Space' : 'Create a Core Collection'}</h1>
            <p className="collection-create-help">Create an on-chain Core collection with your chosen authority and creator royalties. If you have a Space, its details provide editable defaults.</p>



            <div className="new-component-form mint-form collection-create-card">
                <label className="muulti-label" htmlFor="collection-name">Name</label>
                <input
                    className="muulti-input"
                    id="collection-name"
                    type="text"
                    placeholder="Collection Name"
                    value={name}
                    onChange={(e) => { edited.current.name = true; setName(e.target.value); }}
                />
                <label className="muulti-label" htmlFor="collection-description">Description</label>
                <textarea
                    id="collection-description"
                    className="muulti-textarea"
                    placeholder="Collection Description"
                    value={description}
                    onChange={(e) => { edited.current.description = true; setDescription(e.target.value); }}
                />
                <label className="muulti-label">Collection authority wallet
                    <input className="muulti-input" value={authorityWallet} onChange={event => { edited.current.authority = true; setAuthorityWallet(event.target.value); }} />
                </label>
                <div className="attributes-list">
                    <h4>Default NFT attributes (optional)</h4>
                    <p className="collection-create-help">These are editable suggestions for new NFTs in this collection.</p>
                    {attributes.map((attribute, index) => <div key={index} className="attribute-row">
                        <input className="muulti-input" aria-label={`Default attribute ${index + 1} name`} value={attribute.name}
                            onChange={event => setAttributes(values => values.map((value, i) => i === index ? { ...value, name: event.target.value } : value))} />
                        <input className="muulti-input" aria-label={`Default attribute ${index + 1} value`} value={attribute.value}
                            onChange={event => setAttributes(values => values.map((value, i) => i === index ? { ...value, value: event.target.value } : value))} />
                        <button type="button" className="muulti-button" onClick={() => setAttributes(values => values.filter((_, i) => i !== index))}>Remove attribute</button>
                    </div>)}
                    <button type="button" className="muulti-button" onClick={() => setAttributes(values => [...values, { name: '', value: '' }])}>Add default attribute</button>
                </div>
                <label className="collection-create-checkbox"><input type="checkbox" checked={supportsCompressed} onChange={event => setSupportsCompressed(event.target.checked)} /> Enable compressed NFTs in this collection</label>
                <p className="collection-create-help">The configured publishing delegate can mint and update collection content. Your authority wallet remains the collection authority.</p>
                <h4>Image</h4>
                {!file && sourceImageUrl && <p className="collection-create-help">Using the Space image. Choose a file below to replace it.</p>}
                {/* Drag & Drop + Preview */}
                <button
                    type="button"
                    className={`collection-create-dropzone${isDragActive ? ' is-active' : ''}`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => document.getElementById('collection-file-input').click()}
                >

                    <strong>Drag & drop an image here</strong>
                    <span className="collection-create-help">or choose an image file</span>
                    {previewUrl ? <img src={previewUrl} alt="Collection artwork preview" className="collection-create-preview" /> : null}
                </button>
                <input
                    id="collection-file-input"
                    aria-label="Collection artwork"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onPick}
                />

                {/* Royalties */}
                <div className="collection-create-royalties">
                    <h4>Royalties (secondary sales)</h4>
                    <label className="muulti-label">
                        Total Royalties (%)
                        <input
                            className="muulti-input"
                            type="number"
                            min="0"
                            max="20"
                            step="0.1"
                            value={royaltyPercent}
                            onChange={(e) => setRoyaltyPercent(Number(e.target.value))}
                        />
                    </label>
                    <div className="collection-create-help">
                        {royaltyPercent}% total. You (the collection creator) are always included; additional recipients below must sum to ≤ 100%.
                    </div>

                    <div className="collection-create-owner">
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>You (auto)</div>
                        <div className="collection-create-help">
                            Percentage: <strong>{Math.max(0, 100 - Math.min(100, othersTotal))}%</strong> (100% − sum(other creators))
                        </div>
                    </div>

                    <CreatorsEditor
                        otherCreators={otherCreators}
                        onAdd={addCreator}
                        onUpdate={updateCreator}
                        onRemove={removeCreator}
                        othersTotal={othersTotal}
                    />
                </div>

                <div className="muulti-container-button-container collection-create-actions">
                    {isDevnet && (
                        <button
                            className="muulti-button"
                            onClick={handleAirdrop}
                            disabled={airdropping || !connected}
                        >
                            {airdropping ? 'Airdropping…' : 'Devnet Airdrop 1 SOL'}
                        </button>
                    )}
                    <button className="muulti-button collection-create-submit" onClick={handleCreate} disabled={minting || !connected || (!canCreate && !pendingPublishingOperation(spaceId, 'collection'))}>
                        {minting ? 'Creating…' : 'Create Collection'}
                    </button>
                </div>
            </div>

            {/* Checklist */}
            <div className="collection-create-card collection-create-progress">
                <h2>Creation progress</h2>
                <ul className="collection-create-steps">
                    <li>{steps.imageUploaded ? '✅' : '⬜'} Upload image to Irys</li>
                    <li>{steps.metadataUploaded ? '✅' : '⬜'} Upload metadata to Irys</li>
                    <li>{steps.serviceFeePaid ? 'Done:' : 'Pending:'} Collection service fee: {configuration?.fees.collectionCreationFeeEnabled ? formatAtomic(configuration.fees.collectionCreationFeeLamports) + ' SOL' : 'disabled'}</li>
                    <li>{steps.collectionCreated ? '✅' : '⬜'} Create Collection</li>
                    <li>{steps.royaltiesAttached ? '✅' : '⬜'} Attach Royalties</li>
                </ul>


            {/* Links */}
            {txSig && (
                <div style={{ marginTop: 12 }}>
                    <div><strong>Transaction:</strong></div>
                    <a
                        href={`https://explorer.solana.com/tx/${txSig}?cluster=${isDevnet ? 'devnet' : 'mainnet'}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        View on Solana Explorer
                    </a>
                </div>
            )}
            {collectionPk && (
                <div style={{ marginTop: 8 }}>
                    <div><strong>Collection:</strong></div>
                    <a
                        href={`https://core.metaplex.com/explorer/collection/${collectionPk}?env=${isDevnet ? 'devnet' : 'mainnet'}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        View on Metaplex Explorer
                    </a>
                </div>
            )}
            {(imageUri || metadataUri) && (
                <div style={{ marginTop: 8 }}>
                    <div><strong>Uploads (Irys)</strong></div>
                    {imageUri && <div>Image: <a href={imageUri} target="_blank" rel="noreferrer">{imageUri}</a></div>}
                    {metadataUri && <div>Metadata: <a href={metadataUri} target="_blank" rel="noreferrer">{metadataUri}</a></div>}
                </div>
            )}

                {error && <p className="collection-create-error" role="alert">{error}</p>}
                {fundingTxSig && <p><a href={`https://explorer.solana.com/tx/${fundingTxSig}?cluster=${isDevnet ? 'devnet' : 'mainnet-beta'}`}
                    target="_blank" rel="noreferrer">View upload funding transaction: {fundingTxSig}</a></p>}
                {status && <p className="collection-create-status" role="status">{status}</p>}
        </div>
        </div>
    );
}

// Small sub-component to edit "other creators"
function CreatorsEditor({ otherCreators, onAdd, onUpdate, onRemove, othersTotal }) {
    return (
        <div className="collection-create-creators">
            <div style={{ fontWeight: 600 }}>Other creators</div>
            {otherCreators.map((r, i) => (
                <div key={i} className="collection-create-recipient">
                    <input
                        className="muulti-input"
                        aria-label={`Creator ${i + 1} wallet address`}
                        placeholder="Recipient public key"
                        value={r.address}
                        onChange={(e) => onUpdate(i, 'address', e.target.value)}
                    />
                    <input
                        className="muulti-input"
                        aria-label={`Creator ${i + 1} royalty share (%)`}
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={r.percentage}
                        onChange={(e) => onUpdate(i, 'percentage', e.target.value)}
                    />
                    <button className="muulti-button cancel-button" onClick={() => onRemove(i)}>Remove</button>
                </div>
            ))}
            <button className="muulti-button" onClick={onAdd}>Add Creator</button>
            <div className={othersTotal <= 100 ? 'collection-create-help' : 'collection-create-error'}>
                Sum of other creators: <b>{othersTotal}%</b> (must be ≤ 100%)
            </div>
        </div>
    );
}
